/* eslint-disable */

import { TUnitType, UnitType } from '../api/analytics/UnitType';
import { TreemapData } from '../types/charts/chart-options';
import { HeatMapSeriesDataTypes } from '../types/charts/multi-series-data-options';
import { IIndexable } from '../types/general';
import { calculatePercentage, ChartDataResponse } from './chart.helper';
import { highchartColorDefaults } from './highchart-color-defaults.helper';

type Coordinate = {
	x: number;
	y: number;
	value: number | null; // Assuming value can be a number or null
};

export const formatDefaultMultiSeriesData = (
	chartData: IIndexable[],
	unitType?: TUnitType,
	numberOfDecimals?: number,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean
) => {
	let response: ChartDataResponse = newChartResponseObject();

	response = populateAxisArrays(chartData, response, showPercentages, grandTotal);

	if (chartData) {
		response.seriesData = response.yAxisArray.map((item) => {
			const groupedItems = chartData.filter((data) => {
				const keys = Object.keys(data);
				return data[keys[1]] === item;
			});

			return {
				name: item,
				data: response.xAxisArray.map((xAxisItem) => {
					const valueItem = groupedItems.find((groupItem) => {
						const keys = Object.keys(groupItem);
						return groupItem[keys[0]] === xAxisItem;
					});

					if (valueItem) {
						const keys = Object.keys(valueItem);

						// raw numeric value directly from data
						const rawValue = keys[2] ? Number(valueItem[keys[2]]) || 0 : 0;

						const percentage = calculatePercentage(
							keys[2]?.replace(/([A-Z])/g, ' $1').trim(),
							grandTotal,
							rawValue
						);

						// plotted value (y) depends on showPercentages + unitType
						let plotValue = !showPercentages ? rawValue : percentage ?? 0;

						if (!showPercentages && unitType?.code === 'PCT') {
							plotValue = plotValue * 100;
						}

						return {
							y: parseFloat(plotValue.toFixed(numberOfDecimals)),
							value: rawValue, // store raw value here
						};
					} else {
						return { y: 0, value: 0 };
					}
				}),
			};
		});

		// Create a new array to mimic heatmapData structure used for on chart click for multi-series chart
		const lookupData = [];

		// Add xAxisArray as the first row
		lookupData.push(response.xAxisArray);

		// Split valueArray into chunks based on the length of xAxisArray
		const chunkSize = Math.ceil(response.valueArray.length / response.xAxisArray.length);

		for (let i = 0; i < response.valueArray.length; i += chunkSize) {
			const chunk = response.valueArray.slice(i, i + chunkSize);
			lookupData.push(chunk);
		}

		response.lookupData = lookupData;
	}

	return response;
};

export const formatClusteredBarData = (
	chartData: IIndexable[],
	unitType?: TUnitType,
	numberOfDecimals?: number,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean
): ChartDataResponse => {
	let response: ChartDataResponse = newChartResponseObject();

	response = populateAxisArrays(chartData, response, showPercentages, grandTotal);

	if (chartData) {
		response.xAxisArray.map((xAxis) => {
			const yAxisValues: string[] = [
				...new Set(
					chartData.map((value: IIndexable) => value[Object.keys(value)[1]] as string)
				),
			];

			yAxisValues.map((seriesName: string) => {
				const xyValue: IIndexable | undefined = chartData.find(
					(cd: IIndexable<string>) =>
						cd[Object.keys(cd)[0]] === xAxis && cd[Object.keys(cd)[1]] === seriesName
				);

				let rawValue = 0;
				let plotValue: number | null = null;

				if (xyValue) {
					const keys = Object.keys(xyValue);
					rawValue = keys[2] ? Number(xyValue[keys[2]]) || 0 : 0;

					const percentage = calculatePercentage(
						keys[2]?.replace(/([A-Z])/g, ' $1').trim(),
						grandTotal,
						rawValue
					);

					plotValue = !showPercentages ? rawValue : percentage ?? 0;

					if (!showPercentages && unitType?.code === 'PCT') {
						plotValue = plotValue * 100;
					}

					plotValue = parseFloat(plotValue.toFixed(numberOfDecimals));
				}

				const point: { y: number | null; value?: number } =
					plotValue !== null
						? { y: plotValue, value: rawValue }
						: { y: null, value: undefined };

				const seriesDataIndex = response.seriesData.findIndex((d) => d.name === seriesName);

				if (seriesDataIndex >= 0) {
					// Already exists, push point
					(
						response.seriesData[seriesDataIndex].data as {
							y: number | null;
							value?: number;
						}[]
					).push(point);
				} else {
					// Create new series
					response.seriesData.push({
						name: seriesName,
						data: [point],
					});
				}
			});
		});
	}

	// Clustered bar doesn't need yAxisArray
	response.yAxisArray = [];

	return response;
};

export const formatClusteredColumnData = (
	chartData: IIndexable[],
	unitType?: TUnitType,
	numberOfDecimals?: number,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean
) => {
	let response: ChartDataResponse = newChartResponseObject();

	response = populateAxisArrays(chartData, response, showPercentages, grandTotal);

	if (chartData) {
		response.xAxisArray.map((xAxis) => {
			const yAxisValues: string[] = [
				...new Set(
					chartData.map((value: IIndexable) => value[Object.keys(value)[1]] as string)
				),
			] as string[];

			yAxisValues.map((value) => {
				const xyValue: IIndexable | undefined = chartData.find(
					(cd: IIndexable<string>) =>
						cd[Object.keys(cd)[0]] === xAxis && cd[Object.keys(cd)[1]] === value
				);

				let rawValue = 0;
				let plotValue: number | null = null;

				if (xyValue) {
					const keys = Object.keys(xyValue);
					rawValue = keys[2] ? Number(xyValue[keys[2]]) || 0 : 0;

					const percentage = calculatePercentage(
						keys[2]?.replace(/([A-Z])/g, ' $1').trim(),
						grandTotal,
						rawValue
					);

					plotValue = !showPercentages ? rawValue : percentage ?? 0;

					if (!showPercentages && unitType?.code === 'PCT') {
						plotValue = plotValue * 100;
					}

					plotValue = parseFloat(plotValue.toFixed(numberOfDecimals));
				}

				const seriesDataIndex = response.seriesData.findIndex((d) => d.name === value);

				const point: { y: number | null; value?: number } =
					plotValue !== null
						? { y: plotValue, value: rawValue }
						: { y: null, value: undefined };

				if (seriesDataIndex >= 0) {
					(
						response.seriesData[seriesDataIndex].data as {
							y: number | null;
							value?: number;
						}[]
					).push(point);
				} else {
					if (point.value != 0) {
						response.seriesData.push({
							name: value,
							data: [point],
						});
					}
				}
			});
		});
	}

	// Reset yAxisArray since clustered column doesn't need it
	response.yAxisArray = [];

	return response;
};

export const formatHeatmapData = (
	chartData: IIndexable[],
	unitType?: TUnitType,
	numberOfDecimals?: number,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean
) => {
	let response: ChartDataResponse = newChartResponseObject();

	response = populateAxisArrays(chartData, response, showPercentages, grandTotal, unitType);

	if (chartData) {
		let index = 0;
		const finalArray: (number | string)[][] = [];

		chartData.forEach((value: IIndexable) => {
			const keys = Object.keys(value);

			// ✅ original numeric value
			const rawValue = keys[2] ? Number(value[keys[2]]) || 0 : 0;

			const percentage = calculatePercentage(
				keys[2]?.replace(/([A-Z])/g, ' $1').trim(),
				grandTotal,
				rawValue
			);

			let plotValue = !showPercentages ? rawValue : percentage ?? 0;

			if (!showPercentages && unitType?.code === 'PCT') {
				plotValue = plotValue * 100;
			}

			// ✅ push both plotValue (for y) and rawValue (for value)
			finalArray.push([
				response.xAxisArray.indexOf(value[keys[0]] as string),
				response.yAxisArray.indexOf(value[keys[1]] as string),
				parseFloat(plotValue.toFixed(numberOfDecimals)), // y
				rawValue, // value
			]);

			if (index < response.yAxisArray.length - 1) {
				index = index + 1;
			} else {
				index = 0;
			}
		});

		// Create a map to store existing coordinates
		const coordinatesMap = new Map<string, Coordinate>();

		// Populate the map with existing coordinates from the finalArray
		finalArray.forEach((coordinate) => {
			const key = `${coordinate[0] as number},${coordinate[1] as number}`;
			const existingCoordinate = {
				x: coordinate[0] as number,
				y: coordinate[1] as number,
				value: coordinate[2] as number, // ✅ raw/original value
				plotValue: coordinate[2] as number, // optional: keep plotted value separately if you want
			};

			coordinatesMap.set(key, existingCoordinate);
		});

		// Create an array to store the final heatmap data
		const heatmapData: Coordinate[] = [];

		response.xAxisArray.forEach((_xValue, xIndex) => {
			response.yAxisArray.forEach((_yValue, yIndex) => {
				const key = `${xIndex},${yIndex}`;

				const existingCoordinate = coordinatesMap.get(key);

				if (existingCoordinate) {
					heatmapData.push(existingCoordinate);
				} else {
					heatmapData.push({
						x: xIndex,
						y: yIndex,
						value: 0,
					});
				}
			});
		});

		response.seriesData = [
			{
				name: '',
				data: heatmapData as HeatMapSeriesDataTypes,
			},
		];
	}

	return response;
};

export const formatSankeyOrDepWheelMultiSeriesData = (
	chartData: IIndexable[],
	unitType?: TUnitType,
	numberOfDecimals?: number,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean
) => {
	let response: ChartDataResponse = newChartResponseObject();

	response = populateAxisArrays(chartData, response, showPercentages, grandTotal, unitType);
	const unitTypeIsPCT = unitType?.code === 'PCT';

	if (chartData && chartData.length > 0) {
		const keys = Object.keys(chartData[0]);

		const transformedData = chartData.map((item) => {
			const rawValue = parseFloat(item[keys[2]] as string);
			const percentage = calculatePercentage(
				keys[2]?.replace(/([A-Z])/g, ' $1').trim(),
				grandTotal,
				rawValue
			);

			let plotValue = !showPercentages ? rawValue : percentage ?? 0;

			if (!showPercentages && unitTypeIsPCT) {
				plotValue = plotValue * 100;
			}

			plotValue = parseFloat(plotValue.toFixed(numberOfDecimals));

			return [
				item[keys[0]], // Source
				item[keys[1]], // Target
				unitTypeIsPCT ? plotValue : rawValue,
			];
		});

		// Assign seriesData
		response.seriesData = transformedData.map((item) => {
			return { name: '', data: item };
		});

		// Create lookupData similar to other multi-series charts
		const lookupData = [];
		lookupData.push(response.xAxisArray);

		const chunkSize = Math.ceil(response.valueArray.length / response.xAxisArray.length);
		for (let i = 0; i < response.valueArray.length; i += chunkSize) {
			const chunk = response.valueArray.slice(i, i + chunkSize);
			lookupData.push(chunk);
		}
		response.lookupData = lookupData;
	}

	return response;
};

export const formatTreemapStripesData = (
	chartData: IIndexable[],
	unitType?: TUnitType,
	numberOfDecimals?: number,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean
) => {
	let response: ChartDataResponse = newChartResponseObject();
	response = populateAxisArrays(chartData, response, showPercentages, grandTotal);
	const unitTypeIsPCT = unitType?.code === 'PCT';
	const singleSeriesFinalArray: TreemapData = { data: [] };
	const keysArray: TreemapData = { data: [] };

	if (chartData && chartData[0]) {
		const primarySeriesKey = Object.keys(chartData[0])?.splice(0, 1)[0];
		const firstSeries = Object.keys(chartData[0])[0];

		const groupedData = chartData.reduce((acc, item) => {
			const key = item[firstSeries];
			if (!acc[key]) {
				acc[key] = [];
			}
			acc[key].push(item);
			return acc;
		}, {} as Record<string, typeof chartData>);

		const groupedKeys = Object.keys(groupedData);

		groupedKeys?.forEach((key, index) => {
			keysArray.data.push({
				id: key,
				name: key,
				color: highchartColorDefaults[index % highchartColorDefaults.length],
			});
		});

		keysArray.data?.map((key) => {
			singleSeriesFinalArray.data.push(key);

			if (key) {
				response.yAxisArray.map((item) => {
					const groupedItems = chartData.filter((data) => {
						const keys = Object.keys(data);
						return data[keys[1]] === item;
					});

					const valueItem = groupedItems.find((groupItem) => {
						const keys = Object.keys(groupItem);
						return groupItem[keys[0]] === key.id;
					}) as IIndexable<string>;

					if (valueItem) {
						const keys = Object.keys(valueItem);
						const values = Object.values(valueItem);

						// Raw numeric from original data
						const rawValue = keys[2] ? Number(valueItem[keys[2]]) || 0 : 0;

						const percentage = calculatePercentage(
							keys[2]?.replace(/([A-Z])/g, ' $1').trim(),
							grandTotal,
							rawValue
						);

						let plotValue = !showPercentages ? rawValue : percentage ?? 0;

						if (!showPercentages && unitTypeIsPCT) {
							plotValue = plotValue * 100;
						}

						singleSeriesFinalArray.data.push({
							parent: key.id,
							name: values[1] ?? '',
							value: unitTypeIsPCT ? plotValue : rawValue,
							y: parseFloat(plotValue.toFixed(numberOfDecimals)),
							color: key.color,
						});
					}
				});
			}
		});

		response.seriesData = [
			{
				name: primarySeriesKey,
				data: singleSeriesFinalArray.data,
			},
		];
	}

	return response;
};

const newChartResponseObject = (): ChartDataResponse => {
	return {
		xAxisArray: [],
		yAxisArray: [],
		valueArray: [],
		valueArray2: [],
		seriesData: [],
		lookupData: [[], []],
	};
};

const populateAxisArrays = (
	chartData: IIndexable[],
	response: ChartDataResponse,
	showPercentages?: boolean,
	grandTotal?: [{ [key: string]: number }],
	unitType?: UnitType
) => {
	chartData.forEach((value: IIndexable) => {
		const keys = Object.keys(value);
		const xValue = value[keys[0]] as string;
		let yValueRaw = value[keys[1]];
		let rawValue = keys[2] ? Number(value[keys[2]]) || 0 : 0;

		// --- X Axis ---
		if (!response.xAxisArray.includes(xValue)) {
			response.xAxisArray.push(xValue);
		}

		// --- Y Axis ---
		let yValue: string;
		if (Array.isArray(yValueRaw)) {
			yValue = yValueRaw.join(', ');
		} else {
			yValue = String(yValueRaw);
		}

		if (!response.yAxisArray.includes(yValue)) {
			response.yAxisArray.push(yValue);
		}

		// --- Value Array ---
		let valueToPush = rawValue;

		if (showPercentages) {
			const percentage = calculatePercentage(keys[2], grandTotal, rawValue) ?? 0;
			valueToPush = percentage;
		} else if (unitType?.code === 'PCT') {
			valueToPush = rawValue * 100;
		}

		response.valueArray.push(valueToPush.toString());
	});

	return response;
};
